import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  onSearchChange?: (query: string) => void;
}

export default function Navbar({ onSearchChange }: NavbarProps) {
  const location = useLocation();
  const [scrolled, setScrolled] = false;
  const [mobileMenuOpen, setMobileMenuOpen] = false;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: '首页' },
    { path: '/category/all', label: '分类' },
    { path: '/about', label: '关于我们' },
  ];

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-background-50'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-book-open-line text-xl text-primary-500"></i>
            </div>
            <span className="text-lg font-bold font-heading text-foreground-900 whitespace-nowrap">
              韩漫网
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-primary-500'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Search & Mobile Toggle */}
          <div className="flex items-center gap-3">
            {/* Search */}
            {onSearchChange && (
              <div className="hidden sm:flex items-center bg-background-100 rounded-full px-3 py-1.5">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="bg-transparent border-none outline-none text-sm text-foreground-800 placeholder-foreground-400 ml-2 w-36"
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            )}
            {/* Mobile menu button */}
            <button
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="菜单"
            >
              <i
                className={`text-xl text-foreground-700 ${
                  mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                }`}
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 flex flex-col gap-3">
            {/* Mobile Search */}
            {onSearchChange && (
              <div className="flex items-center bg-background-100 rounded-full px-4 py-3 sm:hidden">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="bg-transparent border-none outline-none text-sm text-foreground-800 placeholder-foreground-400 ml-2 w-full"
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            )}
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium py-2 transition-colors duration-200 cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-primary-500'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}